import { useState, useRef, useEffect, useCallback } from "react";
import { useHumanReplies } from "@/hooks/useHumanReplies";
import { Sparkles, Send, RotateCcw, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { whatsappLink } from "@/lib/config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const suggestions = [
  "Faz progressiva?",
  "Quero fazer um corte",
  "Faz restauração capilar?",
  "Quanto custa?",
];

const GREETING =
  "Olá! Sou a assistente da EZ Capelli Studio. Em que posso te ajudar hoje?";

export default function Embed() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: GREETING },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const KEY = "eze_embed_session_v3";
    const stored = sessionStorage.getItem(KEY);
    if (stored) return stored;
    const id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
    return id;
  });
  const scrollRef = useRef(null);
  const [handoffActive, setHandoffActive] = useState(false);
  const appendHuman = useCallback((msgs) => setMessages((m) => [...m, ...msgs]), []);
  useHumanReplies(sessionId, handoffActive, appendHuman, setHandoffActive);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg,
      });
      if (res.data.handoff_active) setHandoffActive(true);
      if (!res.data.reply) return;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: res.data.reply,
          handoff: res.data.handoff,
          wa_message: res.data.wa_message,
        },
      ]);
    } catch {
      toast.error("Ops, não consegui responder. Tente de novo.");
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Desculpe, tive um probleminha técnico. Pode tentar novamente? 🙈",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    const newId = crypto.randomUUID();
    sessionStorage.setItem("eze_embed_session_v3", newId);
    setHandoffActive(false);
    setSessionId(newId);
    setMessages([{ role: "assistant", content: GREETING }]);
    toast.success("Conversa reiniciada");
  };

  return (
    <div
      data-testid="embed-page"
      className="h-screen w-screen bg-[#080808] flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#222] bg-[#0A0A0A] flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
            <Sparkles size={18} className="text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-white text-sm font-medium">Assistente EZ Capelli</div>
            <div className="flex items-center gap-1.5 text-xs text-[#D4AF37]/80">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
              Online agora
            </div>
          </div>
        </div>
        <button
          data-testid="embed-reset-btn"
          onClick={reset}
          className="text-white/60 hover:text-[#D4AF37] p-1.5"
          aria-label="Reiniciar conversa"
          title="Reiniciar conversa"
        >
          <RotateCcw size={16} strokeWidth={1.5} />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid="embed-messages"
        className="flex-1 overflow-y-auto px-5 py-4 space-y-4"
      >
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#D4AF37] text-[#050505] rounded-[18px] rounded-br-sm"
                  : m.role === "human"
                  ? "bg-[#1a1608] border border-[#D4AF37]/50 text-white rounded-[18px] rounded-bl-sm"
                  : "bg-[#141414] border border-[#222] text-white/90 rounded-[18px] rounded-bl-sm"
              }`}
            >
              {m.role === "human" && (
                <span data-testid="embed-human-label" className="block text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Profissional</span>
              )}
              {m.content}
            </div>
          </div>
        ))}

        {/* Handoff CTA */}
        {messages.length > 0 &&
          messages[messages.length - 1].role === "assistant" &&
          messages[messages.length - 1].handoff && (
            <div data-testid="embed-handoff-cta" className="animate-slide-up pt-2">
              <a
                href={whatsappLink(messages[messages.length - 1].wa_message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="embed-handoff-btn"
                className="flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#1fb354] text-white px-4 py-3.5 shadow-[0_8px_24px_rgba(37,211,102,0.35)] transition-all hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-2.5">
                  <svg viewBox="0 0 32 32" width="20" height="20" fill="currentColor" aria-hidden="true">
                    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.015-.53-.015-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.017 2.264v.1c-.015.966.41 1.873.996 2.606 1.34 1.676 3 3.157 4.942 4.007.545.237 2.085.982 2.678.982.539 0 1.428-.213 1.747-.543.288-.302.53-.67.53-1.085 0-.1-.015-.186-.03-.258-.087-.302-1.447-.992-1.717-.992m-2.953 5.837a8.998 8.998 0 0 1-4.685-1.288L10.5 21.07l-3.15.988.985-3.073-.23-.358a9.03 9.03 0 0 1-1.415-4.85c0-4.986 4.07-9.054 9.057-9.054 2.413 0 4.68.95 6.38 2.64a8.98 8.98 0 0 1 2.65 6.426c0 5-4.067 9.055-9.05 9.055m0-19.943c-6 0-10.88 4.876-10.88 10.87 0 1.92.502 3.792 1.462 5.425L5.2 27.5l5.373-1.408a10.89 10.89 0 0 0 5.2 1.325c6.003 0 10.88-4.876 10.88-10.87 0-2.903-1.132-5.63-3.188-7.685a10.843 10.843 0 0 0-7.68-3.19" />
                  </svg>
                  <div>
                    <div className="text-sm font-semibold">Abrir WhatsApp agora</div>
                    <div className="text-[10px] uppercase tracking-wider opacity-90">
                      Falar com a profissional
                    </div>
                  </div>
                </div>
                <ArrowRight size={18} strokeWidth={1.75} />
              </a>
            </div>
          )}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#141414] border border-[#222] rounded-[18px] rounded-bl-sm px-4 py-3">
              <div className="flex gap-1.5">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </div>
            </div>
          </div>
        )}

        {messages.length === 1 && !loading && (
          <div className="pt-2 space-y-2">
            <p className="text-xs text-white/40 uppercase tracking-wider">Sugestões</p>
            {suggestions.map((s) => (
              <button
                key={s}
                data-testid={`embed-suggestion-${s.slice(0, 10)}`}
                onClick={() => send(s)}
                className="block w-full text-left text-sm text-[#D4AF37] border border-[#D4AF37]/30 px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      {handoffActive && (
        <div data-testid="embed-handoff-banner" className="px-4 py-2 text-[11px] text-[#D4AF37] bg-[#D4AF37]/10 border-t border-[#D4AF37]/30">
          Atendimento com a profissional — a assistente virtual está pausada.
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="p-3 border-t border-[#222] flex items-center gap-2 bg-[#0A0A0A] flex-shrink-0"
      >
        <input
          data-testid="embed-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Digite sua mensagem..."
          className="flex-1 bg-[#141414] border border-[#222] focus:border-[#D4AF37] outline-none text-white text-sm px-4 py-2.5 transition-colors"
          disabled={loading}
        />
        <button
          data-testid="embed-send-btn"
          type="submit"
          disabled={loading || !input.trim()}
          aria-label="Enviar"
          className="bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-40 disabled:cursor-not-allowed text-[#050505] p-2.5 transition-colors"
        >
          <Send size={18} strokeWidth={1.5} />
        </button>
      </form>
    </div>
  );
}
